import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './../components/ui/button';
import { toast } from 'sonner';
import { CampaignDialog } from '../components/CampaignDialog';
import { PaceEditor } from '../components/CampaignPaceEditor';
import { BrokerDiscussions } from '../components/BrokerDiscussions';
import { UnansweredBrokerMails } from '../components/UnansweredBrokerMails';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import {
  Loader2, Megaphone, Pause, Play, Trash2, ChevronDown, ChevronRight,
  Paperclip, Clock, CheckCircle2, Reply, XCircle, Ban, Mail, Gauge, MailWarning,
  Layers, Target,
} from 'lucide-react';

const STATUS_STYLES = {
  draft: 'border-border bg-secondary/50 text-muted-foreground',
  active: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  paused: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  completed: 'border-sky-500/40 bg-sky-500/10 text-sky-300',
  cancelled: 'border-red-600/40 bg-red-600/10 text-red-400',
};
const REC_STYLES = {
  active: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  replied: 'border-sky-500/40 bg-sky-500/10 text-sky-300',
  paused: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  completed: 'border-border bg-secondary/50 text-foreground/70',
  cancelled: 'border-border bg-secondary/40 text-muted-foreground',
  failed: 'border-red-600/40 bg-red-600/10 text-red-400',
  bounced: 'border-orange-500/40 bg-orange-500/10 text-orange-300',
};

const fmtDt = (iso) => iso
  ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—';

const paceInfo = (p) => ({
  preset: p?.preset || 'fast',
  min: Math.round(((p?.min_gap_s) || 120) / 60),
  max: Math.round(((p?.max_gap_s) || 600) / 60),
  limit: p?.daily_limit || 0,
});
const berlinToday = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date());

function CampaignProgress({ stats }) {
  const total = stats.recipients || 0;
  if (!total) return null;
  const queued = stats.queued || 0;
  const inProgress = Math.max(0, (stats.active || 0) - queued);
  const paused = stats.paused || 0;
  const cancelled = Math.max(0, total - (stats.active || 0) - (stats.replied || 0) - (stats.completed || 0) - (stats.failed || 0) - (stats.bounced || 0) - paused);
  const segs = [
    { n: stats.replied || 0, cls: 'bg-emerald-500', dot: 'bg-emerald-500', label: 'replied' },
    { n: stats.completed || 0, cls: 'bg-sky-500', dot: 'bg-sky-500', label: 'all emails sent, no reply' },
    { n: inProgress, cls: 'bg-amber-500', dot: 'bg-amber-500', label: 'in progress' },
    { n: paused, cls: 'bg-amber-800', dot: 'bg-amber-800', label: 'paused' },
    { n: stats.bounced || 0, cls: 'bg-orange-500', dot: 'bg-orange-500', label: 'bounced' },
    { n: stats.failed || 0, cls: 'bg-red-500', dot: 'bg-red-500', label: 'failed' },
    { n: cancelled, cls: 'bg-zinc-600', dot: 'bg-zinc-600', label: 'stopped' },
    { n: queued, cls: 'bg-border', dot: 'bg-border', label: 'queued' },
  ].filter(s => s.n > 0);
  const pct = Math.round(((stats.contacted || 0) / total) * 100);
  return (
    <div className="mt-3" data-testid="campaign-progress">
      <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
        <span data-testid="campaign-progress-label">
          <span className="text-foreground font-mono font-bold">{stats.contacted || 0}</span> of {total} broker(s) contacted
        </span>
        <span className="font-mono">{pct}%</span>
      </div>
      <div className="flex h-2 w-full rounded-full overflow-hidden bg-secondary border border-border/60" data-testid="campaign-progress-bar">
        {segs.map((s, i) => (
          <div key={i} title={`${s.n} ${s.label}`} className={`${s.cls} h-full`} style={{ width: `${(s.n / total) * 100}%` }} />
        ))}
      </div>
      <div className="flex gap-x-3 gap-y-1 flex-wrap mt-1.5 text-[10px] text-muted-foreground">
        {segs.map((s, i) => (
          <span key={i} className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${s.dot}`} /> {s.n} {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function StepChips({ rec }) {
  return (
    <div className="flex flex-wrap gap-1">
      {rec.steps.map((s, idx) => {
        if (s.status === 'sent') {
          return (
            <span key={idx} title={`Sent ${fmtDt(s.sent_at)}`}
              className="inline-flex items-center gap-1 text-[9px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded px-1.5 py-0.5">
              <CheckCircle2 className="w-3 h-3" /> {idx === 0 ? 'Intro' : `FU${idx}`} · {fmtDt(s.sent_at)}
            </span>
          );
        }
        if (s.status === 'failed') {
          return (
            <span key={idx} title={s.error || 'Send failed'}
              className="inline-flex items-center gap-1 text-[9px] font-mono border border-red-600/40 bg-red-600/10 text-red-400 rounded px-1.5 py-0.5">
              <XCircle className="w-3 h-3" /> {idx === 0 ? 'Intro' : `FU${idx}`} failed
            </span>
          );
        }
        if (s.status === 'pending' && s.scheduled_at && ['active'].includes(rec.status)) {
          return (
            <span key={idx} title="Scheduled"
              className="inline-flex items-center gap-1 text-[9px] font-mono border border-amber-500/40 bg-amber-500/10 text-amber-300 rounded px-1.5 py-0.5">
              <Clock className="w-3 h-3" /> {idx === 0 ? 'Intro' : `FU${idx}`} · {fmtDt(s.scheduled_at)}
            </span>
          );
        }
        return (
          <span key={idx} className="text-[9px] font-mono border border-border bg-secondary/40 text-muted-foreground rounded px-1.5 py-0.5">
            {idx === 0 ? 'Intro' : `FU${idx}`} —
          </span>
        );
      })}
    </div>
  );
}

function RecipientList({ recipients, isAdmin, onStop, onResume }) {
  const [filter, setFilter] = useState('all');
  const isStopped = (r) => ['cancelled', 'failed'].includes(r.status);
  const counts = {
    all: recipients.length,
    replied: recipients.filter(r => r.status === 'replied').length,
    active: recipients.filter(r => r.status === 'active').length,
    paused: recipients.filter(r => r.status === 'paused').length,
    completed: recipients.filter(r => r.status === 'completed').length,
    bounced: recipients.filter(r => r.status === 'bounced').length,
    stopped: recipients.filter(isStopped).length,
  };
  const FILTERS = [
    { key: 'all', label: 'All', on: 'border-foreground/50 bg-secondary text-foreground' },
    { key: 'replied', label: 'Replied', on: 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300' },
    { key: 'active', label: 'Still enrolled', on: 'border-amber-500/50 bg-amber-500/15 text-amber-300' },
    { key: 'paused', label: 'Paused', on: 'border-amber-500/50 bg-amber-500/15 text-amber-300' },
    { key: 'completed', label: 'Completed — no reply', on: 'border-sky-500/50 bg-sky-500/15 text-sky-300' },
    { key: 'bounced', label: 'Bounced', on: 'border-orange-500/50 bg-orange-500/15 text-orange-300' },
    { key: 'stopped', label: 'Stopped/Failed', on: 'border-red-500/50 bg-red-500/15 text-red-300' },
  ].filter(f => f.key === 'all' || counts[f.key] > 0);
  const filtered = recipients.filter(r =>
    filter === 'all' ? true : filter === 'stopped' ? isStopped(r) : r.status === filter);
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-2" data-testid="campaign-insights-line">
        <span className="text-emerald-300 font-medium">{counts.replied} replied</span> — exited the campaign
        {' · '}<span className="text-amber-300 font-medium">{counts.active} still enrolled</span>
        {counts.paused > 0 && <span> · <span className="text-amber-300">{counts.paused} paused</span></span>}
        {counts.completed > 0 && <span> · <span className="text-sky-300">{counts.completed}</span> finished all emails without a reply</span>}
        {counts.bounced > 0 && <span> · <span className="text-orange-300">{counts.bounced} bounced</span></span>}
        {counts.stopped > 0 && <span> · {counts.stopped} stopped</span>}
      </p>
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="campaign-rec-filters">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`campaign-rec-filter-${f.key}`}
            className={`text-[10px] font-mono border rounded-sm px-2 py-1 transition-colors ${filter === f.key ? f.on : 'border-border text-muted-foreground hover:text-foreground'}`}>
            {f.label} ({counts[f.key]})
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <p className="text-xs text-muted-foreground italic py-3 text-center">No brokers in this group.</p>
      ) : (
        <div className="space-y-1.5">
          {filtered.map((r, j) => (
            <div key={r.id} className="flex items-center gap-3 px-2.5 py-2 bg-secondary/20 border border-border/60 rounded-sm flex-wrap"
              data-testid={`campaign-recipient-${j}`}>
              <div className="min-w-[160px]">
                <p className="text-xs text-foreground font-medium">{r.contact_name || r.contact_email}</p>
                <p className="text-[10px] text-muted-foreground">{r.contact_email}</p>
              </div>
              <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 uppercase ${REC_STYLES[r.status] || REC_STYLES.completed}`}>
                {r.status}
              </span>
              <StepChips rec={r} />
              {r.status === 'replied' && (
                <span className="text-[10px] text-sky-300 truncate max-w-[260px]" title={r.reply_subject || ''}>
                  {r.replied_at ? `${fmtDt(r.replied_at)}` : ''}{r.reply_subject ? ` · „${r.reply_subject}"` : ''}
                </span>
              )}
              {r.status === 'bounced' && (
                <span className="text-[10px] text-orange-300 truncate max-w-[220px]" title={r.bounce_subject || 'Undeliverable'}>
                  undeliverable{r.bounced_at ? ` · ${fmtDt(r.bounced_at)}` : ''}
                </span>
              )}
              {r.status === 'paused' && r.paused_reason && (
                <span className="text-[10px] text-amber-300 truncate max-w-[300px]" title={r.paused_reason}
                  data-testid={`campaign-recipient-paused-reason-${j}`}>
                  {r.paused_reason}
                </span>
              )}
              {isAdmin && r.status === 'active' && (
                <button onClick={() => onStop(r.id)}
                  data-testid={`campaign-recipient-stop-${j}`} title="Stop follow-ups for this broker"
                  className="ml-auto text-muted-foreground hover:text-red-400 p-1"><Ban className="w-3.5 h-3.5" /></button>
              )}
              {isAdmin && r.status === 'paused' && (
                <button onClick={() => onResume(r.id)}
                  data-testid={`campaign-recipient-resume-${j}`} title="Resume follow-ups for this broker"
                  className="ml-auto text-muted-foreground hover:text-emerald-400 p-1"><Play className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CampaignsPage() {
  const { isAdmin } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [discKey, setDiscKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [brokers, setBrokers] = useState([]);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [openingStart, setOpeningStart] = useState(false);
  const [paceFor, setPaceFor] = useState(null);
  const [paceDraft, setPaceDraft] = useState(null);
  const [paceSaving, setPaceSaving] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [trancheFor, setTrancheFor] = useState(null);
  const [trancheCount, setTrancheCount] = useState('');
  const [trancheSaving, setTrancheSaving] = useState(false);

  const load = useCallback(() => {
    api.listCampaigns()
      .then(r => setCampaigns(r.data.campaigns || []))
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to load campaigns'))
      .finally(() => setLoading(false));
    api.getCampaignCoverage().then(r => setCoverage(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [load]);

  const openStart = async () => {
    setOpeningStart(true);
    try {
      const r = await api.getCampaignBrokerPool();
      const list = r.data.brokers || [];
      if (!list.length) {
        toast.error('No broker contacts yet — add brokers on the Contacts page first (type "Broker")');
        return;
      }
      setBrokers(list);
      setCampaignOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load brokers');
    } finally { setOpeningStart(false); }
  };

  const toggleExpand = async (cid) => {
    if (expanded === cid) { setExpanded(null); setDetail(null); return; }
    setExpanded(cid);
    setDetailLoading(true);
    try {
      const r = await api.getCampaign(cid);
      setDetail(r.data);
    } catch { toast.error('Failed to load campaign detail'); }
    setDetailLoading(false);
  };

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); if (expanded) { const r = await api.getCampaign(expanded); setDetail(r.data); } }
    catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };

  const openPace = (c) => {
    const p = paceInfo(c.pace);
    setPaceDraft({ preset: p.preset, min_gap_min: p.min, max_gap_min: p.max, daily_limit: p.limit || '' });
    setPaceFor(c);
  };

  const savePace = async () => {
    setPaceSaving(true);
    try {
      const r = await api.updateCampaignPace(paceFor.id, { pace: {
        ...paceDraft, daily_limit: paceDraft.daily_limit === '' ? 0 : paceDraft.daily_limit } });
      toast.success(r.data.rescheduled
        ? `Pace updated — ${r.data.rescheduled} pending intro email(s) rescheduled`
        : 'Pace updated');
      setPaceFor(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update pace'); }
    setPaceSaving(false);
  };

  const addTranche = async () => {
    const n = parseInt(trancheCount, 10);
    if (!n || n < 1) { toast.error('Enter how many brokers to enroll'); return; }
    setTrancheSaving(true);
    try {
      const r = await api.addCampaignTranche(trancheFor.id, { count: n });
      const d = r.data;
      toast.success(`Tranche enrolled — ${d.added} broker(s) added${d.added < d.requested ? ` (only ${d.added} were still available)` : ''} · ${d.remaining_pool} remaining${d.skipped_firm_replied ? ` · ${d.skipped_firm_replied} übersprungen (Firma hat bereits geantwortet)` : ''}`);
      if (d.firm_warnings?.length) {
        toast.warning(`Gleiche Firma mehrfach: ${d.firm_warnings.slice(0, 3).join(' · ')}${d.firm_warnings.length > 3 ? ` +${d.firm_warnings.length - 3} weitere` : ''}`, { duration: 12000 });
      }
      const cid = trancheFor.id;
      setTrancheFor(null);
      load();
      if (expanded === cid) { const dr = await api.getCampaign(cid); setDetail(dr.data); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add tranche'); }
    setTrancheSaving(false);
  };

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="campaigns-page">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground tracking-tight">Broker Campaigns</h1>
          <p className="text-muted-foreground text-sm mt-1">
            One click messages every broker on your Contacts list — adjustable sending pace,
            Mon–Fri 09:00–17:00 Berlin, follow-ups stop automatically on reply.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openStart} disabled={openingStart} data-testid="campaigns-start-btn"
            className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm text-sm h-9 px-4 shrink-0">
            {openingStart ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Megaphone className="w-4 h-4 mr-1.5" />}
            Start Campaign
          </Button>
        )}
      </div>

      <UnansweredBrokerMails onDiscussionAdded={() => setDiscKey(k => k + 1)} />

      <BrokerDiscussions key={discKey} />

      {coverage && coverage.total_brokers > 0 && (
        <div className="bg-card border border-border rounded-sm p-4" data-testid="broker-coverage-banner">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
            <span className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Target className="w-4 h-4 text-sky-400" /> Goal: reach every broker
            </span>
            <span className="text-xs text-muted-foreground" data-testid="broker-coverage-numbers">
              <span className="text-foreground font-mono font-bold">{coverage.enrolled}</span> of {coverage.total_brokers} enrolled
              {' · '}<span className={`font-mono font-bold ${coverage.remaining > 0 ? 'text-sky-300' : 'text-emerald-300'}`}>{coverage.remaining}</span> remaining
              {coverage.no_email > 0 && <span className="text-amber-400"> · {coverage.no_email} without email</span>}
              {coverage.bounced > 0 && <span className="text-orange-300"> · {coverage.bounced} bounced</span>}
            </span>
          </div>
          <div className="flex h-2 w-full rounded-full overflow-hidden bg-secondary border border-border/60" data-testid="broker-coverage-bar">
            <div className="bg-sky-500 h-full" style={{ width: `${Math.round((coverage.enrolled / coverage.total_brokers) * 100)}%` }} />
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : campaigns.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm py-14 text-center">
          <Megaphone className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No campaigns yet — click "Start Campaign" to message every broker on your list.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c, i) => (
            <div key={c.id} className="bg-card border border-border rounded-sm" data-testid={`campaign-card-${i}`}>
              <div className="p-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <button onClick={() => toggleExpand(c.id)} className="flex items-center gap-2 text-left" data-testid={`campaign-expand-${i}`}>
                    {expanded === c.id ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    <span className="font-heading text-base text-foreground">{c.name}</span>
                  </button>
                  <span className={`text-[10px] font-mono border rounded px-1.5 py-0.5 uppercase ${STATUS_STYLES[c.status] || STATUS_STYLES.draft}`}
                    data-testid={`campaign-status-${i}`}>{c.status}</span>
                  {c.is_test && (
                    <span className="text-[10px] font-mono border border-violet-500/40 bg-violet-500/10 text-violet-300 rounded px-1.5 py-0.5" data-testid={`campaign-test-badge-${i}`}>TEST</span>
                  )}
                  {c.attachment && (
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Paperclip className="w-3 h-3" />{c.attachment.filename}</span>
                  )}
                  <span className="text-[10px] text-muted-foreground ml-auto">Sender: {c.sender_account || c.sender_name || '—'}</span>
                  {(c.cc_emails || []).length > 0 && (
                    <span className="text-[10px] text-emerald-300/80" title={c.cc_emails.join(', ')} data-testid={`campaign-cc-${i}`}>
                      CC: {c.cc_emails.length}
                    </span>
                  )}
                </div>
                {c.paused_reason && <p className="text-xs text-amber-400 mt-1.5">{c.paused_reason}</p>}

                <CampaignProgress stats={c.stats} />

                <div className="flex items-center gap-4 mt-3 flex-wrap text-xs">
                  <span className="text-foreground/80 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-sky-400" />
                    <span className="font-mono font-bold text-foreground">{c.stats.emails_sent}</span> sent
                  </span>
                  <span className="text-foreground/80 flex items-center gap-1.5">
                    <Reply className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="font-mono font-bold text-emerald-300" data-testid={`campaign-replies-${i}`}>{c.stats.replied}</span>
                    replies{c.stats.reply_rate ? ` (${c.stats.reply_rate}%)` : ''}
                  </span>
                  {(c.stats.contacted || 0) > 0 && (
                    <span className="text-foreground/80 flex items-center gap-1.5" data-testid={`campaign-deliverability-${i}`}>
                      <MailWarning className="w-3.5 h-3.5 text-orange-400" />
                      <span className={`font-mono font-bold ${c.stats.bounced ? 'text-orange-300' : 'text-foreground'}`}>{c.stats.bounced || 0}</span>
                      bounced{c.stats.delivered_rate != null ? ` · ${c.stats.delivered_rate}% delivered` : ''}
                    </span>
                  )}
                  <span className="text-foreground/80 flex items-center gap-1.5" data-testid={`campaign-pace-${i}`}>
                    <Gauge className="w-3.5 h-3.5 text-violet-400" />
                    {paceInfo(c.pace).min}–{paceInfo(c.pace).max} min
                    {paceInfo(c.pace).limit > 0 && (
                      <span className="text-muted-foreground">
                        · max {paceInfo(c.pace).limit}/day
                        {c.sent_today?.date === berlinToday() ? ` (${c.sent_today.count} today)` : ''}
                      </span>
                    )}
                  </span>
                  {(c.tranches || []).length > 0 && (
                    <span className="text-foreground/80 flex items-center gap-1.5" data-testid={`campaign-tranches-${i}`}
                      title={(c.tranches || []).map(t => `${t.count} on ${fmtDt(t.at)}`).join(' · ')}>
                      <Layers className="w-3.5 h-3.5 text-sky-400" />
                      {(c.tranches || []).length} tranche{(c.tranches || []).length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {c.status === 'active' && c.stats.next_due_at && (
                    <span className="text-amber-300 flex items-center gap-1"><Clock className="w-3 h-3" /> next send ~{fmtDt(c.stats.next_due_at)}</span>
                  )}
                  {isAdmin && (
                    <span className="ml-auto flex gap-1.5">
                      {['draft', 'active', 'paused'].includes(c.status) && !c.is_test && (
                        <Button variant="outline" size="sm" onClick={() => { setTrancheCount(''); setTrancheFor(c); }}
                          data-testid={`campaign-tranche-btn-${i}`}
                          className="border-sky-600/40 text-sky-300 hover:bg-sky-600/10 rounded-sm text-[10px] h-7 px-2">
                          <Layers className="w-3 h-3 mr-1" /> Add tranche
                        </Button>
                      )}
                      {['draft', 'active', 'paused'].includes(c.status) && !c.is_test && (
                        <Button variant="outline" size="sm" onClick={() => openPace(c)} data-testid={`campaign-pace-btn-${i}`}
                          className="border-violet-600/40 text-violet-300 hover:bg-violet-600/10 rounded-sm text-[10px] h-7 px-2">
                          <Gauge className="w-3 h-3 mr-1" /> Pace
                        </Button>
                      )}
                      {c.status === 'active' && (
                        <Button variant="outline" size="sm" onClick={() => act(() => api.pauseCampaign(c.id), 'Campaign paused')}
                          data-testid={`campaign-pause-${i}`}
                          className="border-amber-600/40 text-amber-400 hover:bg-amber-600/10 rounded-sm text-[10px] h-7 px-2">
                          <Pause className="w-3 h-3 mr-1" /> Pause
                        </Button>
                      )}
                      {c.status === 'paused' && (
                        <Button variant="outline" size="sm" onClick={() => act(() => api.resumeCampaign(c.id), 'Campaign resumed')}
                          data-testid={`campaign-resume-${i}`}
                          className="border-emerald-600/40 text-emerald-400 hover:bg-emerald-600/10 rounded-sm text-[10px] h-7 px-2">
                          <Play className="w-3 h-3 mr-1" /> Resume
                        </Button>
                      )}
                      {['draft', 'active', 'paused'].includes(c.status) && (
                        <Button variant="outline" size="sm"
                          onClick={() => window.confirm(`Cancel campaign "${c.name}"? Remaining emails will not be sent.`) && act(() => api.cancelCampaign(c.id), 'Campaign cancelled')}
                          data-testid={`campaign-cancel-${i}`}
                          className="border-red-600/40 text-red-400 hover:bg-red-600/10 rounded-sm text-[10px] h-7 px-2">
                          <Trash2 className="w-3 h-3 mr-1" /> Cancel
                        </Button>
                      )}
                      {['completed', 'cancelled'].includes(c.status) && (
                        <Button variant="outline" size="sm"
                          onClick={() => window.confirm(`Delete campaign "${c.name}" permanently?${c.is_test ? '' : ' Contacted brokers stay protected from being enrolled twice.'}`)
                            && act(() => api.purgeCampaign(c.id), 'Campaign deleted')}
                          data-testid={`campaign-delete-${i}`}
                          className="border-red-600/40 text-red-400 hover:bg-red-600/10 rounded-sm text-[10px] h-7 px-2">
                          <Trash2 className="w-3 h-3 mr-1" /> Delete
                        </Button>
                      )}
                    </span>
                  )}
                </div>
              </div>

              {expanded === c.id && (
                <div className="border-t border-border p-4" data-testid="campaign-detail">
                  {detailLoading ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                  ) : detail && detail.campaign.id === c.id ? (
                    <RecipientList recipients={detail.recipients} isAdmin={isAdmin}
                      onStop={(rid) => act(() => api.stopCampaignRecipient(rid), 'Follow-ups stopped for this broker')}
                      onResume={(rid) => act(() => api.resumeCampaignRecipient(rid), 'Broker resumed — next email rescheduled')} />
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <CampaignDialog open={campaignOpen} onOpenChange={setCampaignOpen}
        contacts={brokers} allBrokers onStarted={load} />

      <Dialog open={!!trancheFor} onOpenChange={(o) => !o && setTrancheFor(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="tranche-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground flex items-center gap-2">
              <Layers className="w-5 h-5 text-sky-400" /> Add tranche — {trancheFor?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Enrolls the next batch of brokers who are not yet part of any campaign — double enrollment is impossible.
            {coverage && (
              <span className="block mt-1 text-sky-300" data-testid="tranche-remaining-info">
                {coverage.remaining} broker(s) still available.
              </span>
            )}
          </p>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">How many brokers in this tranche?</label>
            <Input type="number" min="1" value={trancheCount} onChange={e => setTrancheCount(e.target.value)}
              placeholder="e.g. 100" data-testid="tranche-count-input"
              className="bg-secondary border-border text-foreground rounded-sm h-9" />
          </div>
          <p className="text-[10px] text-muted-foreground">
            New recipients use this campaign's email texts and sending pace. On a running campaign they are queued after the current sending backlog.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setTrancheFor(null)} disabled={trancheSaving}
              className="border-border text-muted-foreground rounded-sm text-xs h-8">Cancel</Button>
            <Button onClick={addTranche} disabled={trancheSaving || !parseInt(trancheCount, 10)} data-testid="tranche-add-btn"
              className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm text-xs h-8 px-4">
              {trancheSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enroll tranche'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!paceFor} onOpenChange={(o) => !o && setPaceFor(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="pace-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground flex items-center gap-2">
              <Gauge className="w-5 h-5 text-violet-400" /> Sending pace — {paceFor?.name}
            </DialogTitle>
          </DialogHeader>
          {paceDraft && <PaceEditor pace={paceDraft} onChange={setPaceDraft} />}
          <p className="text-[10px] text-muted-foreground">
            Changes apply immediately — pending intro emails are re-spread at the new pace; follow-up delays (days) stay unchanged.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPaceFor(null)} disabled={paceSaving}
              className="border-border text-muted-foreground rounded-sm text-xs h-8">Cancel</Button>
            <Button onClick={savePace} disabled={paceSaving} data-testid="pace-save-btn"
              className="bg-violet-600 hover:bg-violet-500 text-white rounded-sm text-xs h-8 px-4">
              {paceSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save pace'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
